import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Droplets, Loader2, CheckCircle2, History, Wallet, ArrowRight } from "lucide-react";

export default function ActivateBottle() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const loadBalance = async () => {
    try {
      const { data } = await api.get("/credits/balance");
      setBalance(data);
    } catch (e) {}
  };
  useEffect(() => { loadBalance(); }, []);

  const activate = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim();
    if (!trimmed) { setError("Please enter an activation code."); return; }
    setBusy(true);
    try {
      const idempotency_key = crypto.randomUUID();
      const { data } = await api.post("/bottle/activate", { code: trimmed, idempotency_key });
      setResult(data);
      setCode("");
      toast.success("Bottle activated successfully");
      loadBalance();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout title="Bottle Activations">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Certificate Credits</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Activate a SGBINOXE Bottle</h2>
        <p className="mt-1 text-sm text-neutral-500">Each valid SGBINOXE bottle activation code adds 10 installation certificate credits to your account.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {result ? (
            <div className="rounded-2xl border border-emerald-500/20 bg-[#141414] p-8" data-testid="activation-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-400">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">Bottle activated successfully</h3>
              <p className="mt-2 text-sm text-neutral-400">{result.message}</p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-600">Credits Added</p>
                  <p className="mt-1 font-display text-2xl font-semibold text-emerald-400">+{result.credits_added}</p>
                </div>
                <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-600">New Balance</p>
                  <p className="mt-1 font-display text-2xl font-semibold text-white">{result.new_balance}</p>
                </div>
                <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-600">Activation Code</p>
                  <p className="mt-1 font-mono text-sm text-white">{result.masked_code}</p>
                </div>
                <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-600">Activation Date</p>
                  <p className="mt-1 text-sm text-white">{new Date(result.activation_date).toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setResult(null)} className="rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#FF1C2A]" data-testid="activate-another-btn">Activate Another</button>
                <button onClick={() => navigate("/certificates/new")} className="rounded-lg border border-white/[0.1] px-5 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05]">Create Certificate</button>
              </div>
            </div>
          ) : (
            <form onSubmit={activate} className="rounded-2xl border border-white/[0.06] bg-[#141414] p-8" data-testid="activate-form">
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#E30613]/15 text-[#FF1C2A]"><Droplets className="h-[18px] w-[18px]" /></span>
                <h3 className="font-display text-base font-semibold uppercase tracking-wider text-white">Activation Code</h3>
              </div>
              {error && <div className="mb-4 rounded-lg border border-[#E30613]/30 bg-[#E30613]/10 px-4 py-3 text-sm text-[#FF6069]" data-testid="activate-error">{error}</div>}
              <Label className="text-neutral-300">Enter your bottle activation code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="01A-0001-K9Q4X"
                className="mt-2 h-12 rounded-lg border-white/[0.1] bg-[#0A0A0A] font-mono uppercase tracking-widest text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]"
                data-testid="activate-code-input"
              />
              <button type="submit" disabled={busy} className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#E30613] font-medium text-white transition-colors hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="activate-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Activate Bottle</>}
              </button>
            </form>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6">
            <div className="flex items-center gap-2 text-neutral-400"><Wallet className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.18em]">Available Credits</p></div>
            <p className="mt-3 font-display text-4xl font-semibold text-white" data-testid="available-credits">{balance?.available_credits ?? "—"}</p>
            <p className="mt-1 text-sm text-neutral-500">{balance?.bottles_activated ?? 0} bottles activated</p>
          </div>
          <Link to="/activations" className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-[#141414] px-5 py-4 transition-colors hover:bg-white/[0.03]" data-testid="link-activation-history">
            <span className="flex items-center gap-3 text-sm text-neutral-200"><History className="h-4 w-4 text-[#FF1C2A]" /> Activation History</span>
            <ArrowRight className="h-4 w-4 text-neutral-500" />
          </Link>
          <Link to="/credit-history" className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-[#141414] px-5 py-4 transition-colors hover:bg-white/[0.03]" data-testid="link-credit-history">
            <span className="flex items-center gap-3 text-sm text-neutral-200"><Wallet className="h-4 w-4 text-[#FF1C2A]" /> Credit History</span>
            <ArrowRight className="h-4 w-4 text-neutral-500" />
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
